/**
 * Firebase app + Auth initialization and auth helpers (CLAUDE.md §7).
 *
 * Email/password self-registration with email verification; password reset.
 * The Firebase ID token is attached as `Authorization: Bearer` by apiClient.
 */

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  reload,
  type Auth,
  type User,
  type UserCredential,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const firebaseApp: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(firebaseApp);

/** Register a new user and immediately send a verification email. */
export async function register(
  email: string,
  password: string,
): Promise<UserCredential> {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await sendEmailVerification(cred.user);
  return cred;
}

/** Sign in with email + password. */
export function login(
  email: string,
  password: string,
): Promise<UserCredential> {
  return signInWithEmailAndPassword(auth, email, password);
}

/** Sign in with a Google account (popup). Google accounts are pre-verified. */
export function loginWithGoogle(): Promise<UserCredential> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return signInWithPopup(auth, provider);
}

/** Sign out the current user. */
export function logout(): Promise<void> {
  return signOut(auth);
}

/** Send a password-reset email. */
export function resetPassword(email: string): Promise<void> {
  return sendPasswordResetEmail(auth, email);
}

/** Re-send the verification email to the currently signed-in user. */
export async function resendVerification(): Promise<void> {
  if (!auth.currentUser) throw new Error("Not signed in.");
  await sendEmailVerification(auth.currentUser);
}

/** Reload the current user (to pick up a freshly verified emailVerified flag). */
export async function reloadUser(): Promise<User | null> {
  if (!auth.currentUser) return null;
  await reload(auth.currentUser);
  return auth.currentUser;
}

/**
 * Get a fresh Firebase ID token for the current user, or null if signed out.
 * `forceRefresh` re-mints the token (e.g. after a custom-claim change).
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

/** Whether the current user is an admin (Firebase custom claim `admin: true`). */
export async function isAdmin(): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  const result = await user.getIdTokenResult();
  return result.claims.admin === true;
}

/** Subscribe to auth state changes. Returns an unsubscribe fn. */
export function onAuthChange(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, cb);
}

export type { User };
